import 'animate.css/animate.min.css';
import React, { FC, ReactElement, Suspense } from 'react';
import { useEffect } from "react";
import AOS from "aos";
import "aos/dist/aos.css";
import {
  Button, Col,
  Container,
  Figure, ListGroup, Nav, OverlayTrigger, Row
} from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { IconContext } from 'react-icons';
import {
  FaArrowAltCircleUp, FaGithub, FaLinkedin
} from 'react-icons/fa';
import './App.css';
import image from './assets/SC50150.jpg';
import { History } from "./components/history";
import { Hobbies } from "./components/hobbies";
import { MyNavbar } from "./components/navbar";
import { PersonalProjects, ProfessionalProjects } from "./components/projects";
import { Skills } from './components/skills';
import { Travels } from "./components/travels";
import { computeAge, renderTooltip, scrollTo } from './utils';

// tslint:disable-next-line: no-var-requires
require('./global.d.ts');

/**
* LeftHeader, containing general info about author
*/
const LeftHeader: FC = () => {
  const { t } = useTranslation()
  return (
    <div>
      <Row>
        <Col md={12} className="pr-0 pl-0">
          <div className="name">
            <h1>Leeroy Wack</h1>
          </div>
          <div>
            <h5>{t('me.work')}</h5>
          </div>
          <div>
            <h5>{computeAge()} {t('me.years')}</h5>
          </div>
        </Col>
        <Col md={12} className="align-self-center pt-3 pt-md-0 pb-3 pb-md-0 mt-md-5 pr-0 pl-0">
          <div className="name">
            <h5>CNAM Grand-Est</h5>
          </div>
          <div>
            <h5>{t('me.livesIn')}</h5>
          </div>
          <div>
            <a href="mailto:leeroy.wack@gmail.com" target="_blank" rel="noopener noreferrer">
              <Button variant="secondary">leeroy.wack@gmail.com</Button>
            </a>
          </div>
        </Col>
      </Row>
    </div>
  );
}

/**
* RightHeader containing info about Skills
*/
const RightHeader: FC = () => {
  return (
    <Skills />
  );
}

/**
* Display an icon that links to social media
* @param props info about the social media (href, icon)
*/
const IconSocial: FC<{ href: string | undefined, icon: React.ReactNode }> = ({ href, icon }) => {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
    >
      <IconContext.Provider value={{ size: '3em' }}>
        <div>
          {icon}
        </div>
      </IconContext.Provider>
    </a>
  );
}

/**
* AppHeader, containing LeftHeader, Image, and RightHeader
*/
const AppHeader: FC = () => {
  return (
    <Container fluid={true} className="App-header">
      <Row className="content-justify-center">
        <Col sm={12} md={4} className="m-auto text-md-center align-self-center">
          <LeftHeader />
        </Col>
        <Col sm={12} md={4} className="align-self-center">
          <Row >
            <Col>
              <Figure className="mt-3">
                <Figure.Image
                  height="60%"
                  width="60%"
                  alt="171x180"
                  src={image}
                  roundedCircle={true}
                />
              </Figure>
            </Col>
          </Row>
          <Row className="App-header text-center">
            <Col xs={6} className="text-right">
              <IconSocial href="https://fr.linkedin.com/in/leeroy-wack-4bb100168/" icon={<FaLinkedin />} />
            </Col>
            <Col xs={6} className="">
              <IconSocial href="https://github.com/L33ROY" icon={<FaGithub />} />
            </Col>
          </Row>
        </Col>
        <Col sm={12} md={4} className="m-auto text-left align-self-center pl-0">
          <RightHeader />
        </Col>
      </Row>
    </Container>
  );
}

/**
* Footer component
*/
const Footer: FC = () => {
  const { t } = useTranslation();
  const scrollOptions = {
    smooth: true,
    offset: -40,
    duration: 1000,
  };

  return (
    <Container>
      <ListGroup horizontal={true} className="justify-content-center copyright pt-2">
        <ListGroup.Item className="copyright" variant="dark">© 2026 Copyright: Leeroy Wack</ListGroup.Item>
      </ListGroup>
      <Row className="justify-content-center">
        <Col md={3}>
          <Nav.Link className="mt-2" href="#App" onSelect={scrollTo('App', scrollOptions)}>
            <OverlayTrigger
              placement="right"
              delay={{ show: 0, hide: 0 }}
              overlay={renderTooltip(t('top'))}
            >
              <Button variant="outline-light">
                <FaArrowAltCircleUp className="mb-1" />
              </Button>
            </OverlayTrigger>
          </Nav.Link>
        </Col>
      </Row>
    </Container>
  );
}

// page uses the hook
const Page: FC = () => {
  useEffect(() => {
    AOS.init({
      duration: 2000,
      once: true
    });
  }, []);

  return (
    <div id="App" className="App">
      <MyNavbar />

      <AppHeader />

      <section className="grey" data-aos="fade-in">
        <PersonalProjects />
      </section>

      <section className="grey" data-aos="fade-in">
        <ProfessionalProjects />
      </section>

      <section data-aos="fade-in">
        <History />
      </section>

      <section className="grey" data-aos="fade-in">
        <Hobbies />
      </section>

      <section data-aos="fade-in">
        <Travels />
      </section>

      <section className="App-footer">
        <Footer />
      </section>
    </div>
  );
};

// loading component for suspense fallback
const Loader = (): ReactElement => (
  <div className="App" />
);

// here app catches the suspense from page in case translations are not yet loaded
const App: FC = () => {
  return (
    <Suspense fallback={<Loader />}>
      <Page />
    </Suspense>
  );
}
export default App